import { CT_POLLING_FREQUENCY } from 'src/globals';
import { getDuration } from 'src/utils/utils';
import { SHIPMENT_TYPE, viewToggle } from 'src/components/pages/Container/ContainerUtils';
import { AccessType } from 'src/Library/Types';
import { isValidArray, getUniqFromArray, onlyUnique } from 'src/Library/Helper';
import { CardData } from 'src/components/pages/Container/Interfaces/Shipment.interface';
import {
  SHIPMENT_TRACKING_ENABLE_LOADING,
  SHIPMENT_TRACKING_SET_DATA,
  SHIPMENT_TRACKING_ERROR,
  SHIPMENT_TRACKING_FILTERS,
  SHIPMENT_TRACKING_SET_PAGINATION,
  SHIPMENT_TRACKING_RESET_PAGINATION,
  SHIPMENT_TRACKING_RESET_FILTERS,
  SHIPMENT_TRACKING_FETCHING_DATA,
  SHIPMENT_TRACKING_CHECK_CHANGED,
  SHIPMENT_TRACKING_SET_PREV_PAGINATION,
  SHIPMENT_TRACKING_SET_POLLING_REMAINING,
  SHIPMENT_TRACKING_SET_POLLING_DATA,
  SHIPMENT_TRACKING_BATCH_SIZE,
  SHIPMENT_TRACKING_SUMMARY_METRIC_UPDATE,
  SHIPMENT_TRACKING_RESET_DETAIL_DATA,
  SHIPMENT_TRACKING_SEARCH_FILTERS,
  ADVISORY_FETCH_ALL,
  SET_MAP_LIST_VIEW,
} from '../actions/constants';
import {
  getLastSortedColumnValueBl,
  getLastSortedColumnValueContainer,
  getLastBlId,
  getLastSortedColumnValue,
  getLastTrackingContainerId,
  getLastItemId,
} from 'src/utils/pagination';
import {
  fetchAllShipments,
  getShipmentPollData,
  applyFiltersToData,
  getGlobalSummary,
  getContainerList,
  containerPoll,
  getShipmentStatusWiseCount,
  getGlobalSummaryStats,
  fetchAdvisory,
} from 'src/api/shipmentTracking';
import {
  ShipmentTrackingFilters,
  ShipmentTrackingFetchParams,
} from 'src/components/pages/Container/Interfaces/Filters.interface';
import {
  CommonRequest,
  CommonPaginationParams,
  BLRequest,
  ContainerRequest,
  ShipmentRequest,
  ShipmentRequestParams,
  BLRequestParams,
} from 'src/components/pages/Container/Interfaces/ShipmentRequests.interface';
import {
  CommonPagination,
  ShipmentPagination,
  BLPagination,
  ContainerPagination,
} from 'src/components/pages/Container/Interfaces/ShipmentPagination.interface';
import {
  BulkApiRequest,
} from 'src/components/pages/Container/Interfaces/TypesInShipmentTracking';
import * as LogRocket from 'logrocket';
import { batch } from 'react-redux';

export interface Pagination extends ShipmentPagination, BLPagination, ContainerPagination {}
export interface CommonRequestParams extends CommonPaginationParams, CommonRequest {}

let pollingAgent: ReturnType<typeof setTimeout> | null = null;

function getKeys(inputArray): string[] {
  if (!inputArray || !Array.isArray(inputArray)) return [];
  const keyArray = inputArray.map(item => item.key);
  return keyArray;
}

function getAccessTypeFilters(accessTypeFilters: any[]): AccessType[] {
  if (!isValidArray(accessTypeFilters)) return [];
  const accessType = accessTypeFilters.map((item) => {
    if (item.accessType === 'TEAM') {
      return {
        accessType : item.accessType,
        teamId : item.key,
      };
    }
    return { accessType : item.accessType };
  });
  return accessType || [];
}

function getBlReceived(blReceived: string) {
  switch (blReceived) {
    case 'Yes': return true;
    case 'No': return false;
    default: return undefined;
  }
}

export function getCommonFilters(filters: ShipmentTrackingFilters): CommonRequest {
  const { statusList } = filters;
  const commonFilters: CommonRequest = {
    endDate: filters.endDate,
    startDate: filters.startDate,
    statusList: filters.statusList,
    timeFilter: filters.timeFilter,
    blReceived: getBlReceived(filters.blReceived),
    exceptionList: filters.exceptionList,
    shipmentType: filters.shipmentTypeFilter,
    shippingLineList: filters.shippingLineList,
    movementTypeList: filters.movementTypeList,
    trackingStatusList: filters.trackingStatusList,
    timeFilterType: filters.timeFilterType,
    isManuallyTracked: Boolean(filters.isManuallyTracked),
    incotermList: filters.incotermList,
    companyList: filters.companyList,
    businessLocationList: filters.businessLocationList,
    channelList: filters.channelList,
    soNumberList: filters.soNumberList,
    stuffingIdList: filters.stuffingIdList,
    containerSizeList: filters.containerSizeList,
    blIdList: filters.blIdList,
    hblIdList: filters.hblIdList,
    invoiceIdList: filters.invoiceIdList,
    bookingIdList: filters.bookingIdList,
    containerIdList: filters.containerIdList,
    shipmentIdList: filters.shipmentIdList,
    internalReferenceNumberList: filters.internalReferenceNumberList,
    customerReferenceNumberList: filters.customerReferenceNumberList,
    supplierReferenceNumberList: filters.supplierReferenceNumberList,
    executionReferenceNumberList: filters.executionReferenceNumberList,
    shipmentOwner: filters.shipmentOwner,
    ffIdList: getKeys(filters.ffIdList),
    productList: getKeys(filters.products),
    customerList: getKeys(filters.customerList),
    supplierList: getKeys(filters.supplierList),
    shipperIdList: getKeys(filters.shipperIdList),
    originPortList: getKeys(filters.originPortList),
    destinationPortList: getKeys(filters.destinationPortList),
    accessTypeFilters: getAccessTypeFilters(filters.accessTypeFilters),
    isComplete: filters.isComplete || statusList && statusList[0] === 'COMPLETE' ? undefined : false,
    polCountryIds: getKeys(filters.polCountryList),
    podCountryIds: getKeys(filters.podCountryList),
  };
  return commonFilters;
}

function getCommonPagination(pagination: CommonPagination): CommonPaginationParams {
  const commonPagination = {
    nextOrPrev: pagination.nextOrPrev,
    currentPageNumber: pagination.currentPageNumber,
    resultPerPage: pagination.resultPerPage,
    isNextPresent: pagination.isNextPresent,
  };
  return commonPagination;
}

function getBLRequestBody(blRequestParams: BLRequestParams): BLRequest {
  const {
    pagination,
    sortBy,
    descendingOrder,
    isComplete,
    ...commonRequestParams
  } = blRequestParams;

  const blRequest = {
    ...commonRequestParams,
    sortBy,
    isComplete,
    descendingOrder,
    lastSortedColumnValue: getLastSortedColumnValue(pagination),
    lastTrackingRequestId: getLastTrackingContainerId(pagination),
  };
  return blRequest;
}

function getHBLRequestBody(hblRequestParams: BLRequestParams): BLRequest {
  const {
    pagination,
    sortBy,
    descendingOrder,
    isComplete,
    ...commonRequestParams
  } = hblRequestParams;

  const hblRequest = {
    ...commonRequestParams,
    sortBy,
    isComplete,
    descendingOrder,
    lastSortedColumnValue: getLastSortedColumnValue(pagination),
    lastTrackingRequestId: getLastTrackingContainerId(pagination),
  };
  return hblRequest;
}

function getContainerRequest(containerRequestParams: ShipmentRequestParams): ContainerRequest {
  const {
    filters,
    pagination,
    sortBy,
    descendingOrder,
    ...commonRequestParams
   } = containerRequestParams;

  const containerRequest = {
    ...commonRequestParams,
    sortBy,
    descendingOrder,
    addedByUser: Boolean(filters.addedByUser),
    lastContainerId: getLastTrackingContainerId(pagination),
    lastSortedColumnValue: getLastSortedColumnValue(pagination),
  };
  return containerRequest;
}

function getShipmentRequest(shipmentRequestParams: ShipmentRequestParams): ShipmentRequest {
  const {
    filters,
    pagination,
    sortBy,
    descendingOrder,
    ...commonRequestParams
  } = shipmentRequestParams;
  const shipmentRequest = {
    ...commonRequestParams,
    sortBy,
    descendingOrder,
    lastBlId: getLastBlId(pagination),
    isBlNextPresent: pagination.isBlNextPresent,
    isContainerNextPresent: pagination.isContainerNextPresent,
    lastShipmentId: getLastItemId(pagination),
    lastSortedColumnValueContainer: getLastSortedColumnValueContainer(pagination),
    shipmentTypeFilter: filters.shipmentTypeFilter,
    lastSortedColumnValueBl: getLastSortedColumnValueBl(pagination),
    lastSortedColumnValue: getLastSortedColumnValue(pagination),
  };
  return shipmentRequest;
}

function getRequestBody(bodyParams: ShipmentTrackingFetchParams) {
  const { filters: currentFilters, pagination, sortBy, descendingOrder } = bodyParams;
  const filters = { ...currentFilters };
  
  const commonRequestParams = {
    ...getCommonFilters(filters),
    ...getCommonPagination(pagination),
  };

  const shipmentRequestParams = {
    pagination,
    sortBy,
    descendingOrder,
    ...commonRequestParams,
  };

  switch (filters.tabId) {
    case SHIPMENT_TYPE.BL: return getBLRequestBody(
      shipmentRequestParams,
    );
    case SHIPMENT_TYPE.CONTAINER: return getContainerRequest({
      ...shipmentRequestParams,
      filters,
    });
    case SHIPMENT_TYPE.HBL : return getHBLRequestBody(
      {
        ...shipmentRequestParams,
        shipmentType: 'HBL',
      },
    );
    default: return getShipmentRequest({
      ...shipmentRequestParams,
      filters,
    });
  }
}

function getData(tabId: string, apiBody) {
  switch (tabId) {
    case SHIPMENT_TYPE.BL: return applyFiltersToData(apiBody);
    case SHIPMENT_TYPE.CONTAINER: return getContainerList(apiBody);
    case SHIPMENT_TYPE.HBL: return applyFiltersToData(apiBody);
    default: return fetchAllShipments(apiBody);
  }
}

export const fetchAdvisories = () => async (dispatch) => {
  const response = await fetchAdvisory();
  if (response.isSuccess) {
    dispatch({
      type: ADVISORY_FETCH_ALL,
      data: response.data,
    });
  }
}

export const setShipmentStatusCount = (lastPage?: boolean) => async (dispatch, getState) => {
  const { selectedFilters } = getState().shipmentTrackingReducer;
  dispatch({
    type: SHIPMENT_TRACKING_ENABLE_LOADING,
  });
  const {
    pagination,
    prevPagination,
    sortBy,
    descendingOrder,
    data,
    view,
  } = getState().shipmentTrackingReducer[selectedFilters.tabId];
  let reqPagination = pagination;
  if (lastPage) reqPagination = prevPagination;
  let body = getRequestBody({
    sortBy,
    descendingOrder,
    filters: selectedFilters,
    pagination: reqPagination,
  });
  body = {
    ...body,
    shipmentType: SHIPMENT_TYPE.SHIPMENT,
    isVesselTrackingPresent: view === viewToggle.MAP || undefined,
  };
  const response = await getShipmentStatusWiseCount(body);
  let responseToAdd;
  if (response.isSuccess) {
    responseToAdd = {
      data: {
        statusWiseCount: response.data,
      },
    };
  }
  dispatch(handleData(selectedFilters.tabId, responseToAdd?.data));
  dispatch({
    type: SHIPMENT_TRACKING_SET_DATA,
    data: responseToAdd?.data,
    isFetchingData: true,
  });
}

export const setView = (view) => async (dispatch) => {
  batch(() => {
    dispatch({
      data: view,
      type: SET_MAP_LIST_VIEW,
    });
    dispatch(fetchShipmentTrackingData());
  });
}

export const fetchShipmentTrackingData = (lastPage?: boolean) => async (dispatch, getState) => {
  dispatch(stopPolling());
  dispatch({
    type: SHIPMENT_TRACKING_ENABLE_LOADING,
  });
  dispatch(resetSelectedItems());
  const {
    selectedFilters,
  } = getState().shipmentTrackingReducer;
  const { tabId } = selectedFilters;

  const {
    pagination,
    prevPagination,
    sortBy,
    descendingOrder,
    data,
    view,
  } = getState().shipmentTrackingReducer[tabId];
  let reqPagination = pagination;
  if (lastPage) reqPagination = prevPagination;
  const body = getRequestBody({
    sortBy,
    descendingOrder,
    filters: selectedFilters,
    pagination: reqPagination,
  });
  if (view === viewToggle.MAP) {
    body.isVesselTrackingPresent = true;
  }
  let response = await getData(selectedFilters.tabId, body);
  if (response.isSuccess) {
    if (tabId === SHIPMENT_TYPE.SHIPMENT) {
      const body2 = {
        ...body,
        shipmentType: SHIPMENT_TYPE.SHIPMENT,
      };
      const response2 = await getShipmentStatusWiseCount(body2);
      if (response2.isSuccess) {
        response = {
          ...response,
          data: {
            ...response.data,
            statusWiseCount: response2.data,
          },
        };
      }
    }
    dispatch(handleData(selectedFilters.tabId, response.data));
    dispatch({
      type: SHIPMENT_TRACKING_SET_DATA,
      data: response.data,
    });
  } else {
    dispatch({
      type: SHIPMENT_TRACKING_ERROR,
      data: response.errorMessage,
    });
  }
};

function checkOne(disableRefresh: boolean, duration: number) {
  return (
    disableRefresh &&
    (duration || duration === 0) &&
    duration < 5
  );
}

function conditionOne(item: CardData, tabId: string) {
  let duration = 0;
  let disableRefresh = false;
  switch (tabId) {
    case SHIPMENT_TYPE.SHIPMENT: {
      duration =  getDuration(item?.refreshRequestedAt) || 0;
      disableRefresh = item?.disableRefreshRequest;
      if (checkOne(disableRefresh, duration)) {
        item.isTrackingPresent = false;
        item.isTrackingFailed = false;
        return true;
      }
      return false;
    }
    case SHIPMENT_TYPE.CONTAINER: {
      disableRefresh = item?.disableRefreshContainerTrackingRequest;
      duration = getDuration(item?.refreshContainerTrackingRequestedAt) || 0;
      if (checkOne(disableRefresh, duration)) {
        item.isTrackingPresent = false;
        item.isTrackingFailed = false;
        return true;
      }
      return false;
    }
    case SHIPMENT_TYPE.BL:
    default:  {
      duration = getDuration(item?.refreshBlInfoRequestedAt) || 0;
      disableRefresh = item?.disableRefreshBlInfoRequest;
      if (checkOne(disableRefresh, duration)) {
        item.isInfoPresent = false;
        item.isInfoFetchFailed = false;
        return true;
      }
      return false;
    }
  }
}

function conditionTwo(item: CardData, tabId: string) {
  let isTrackingPresent = false;
  let pendingTrackingCount = 0;
  switch (tabId) {
    case SHIPMENT_TYPE.SHIPMENT: {
      isTrackingPresent = item?.isTrackingPresent;
      pendingTrackingCount = item?.pendingTrackingCount;
      break;
    }
    case SHIPMENT_TYPE.CONTAINER: {
      isTrackingPresent = item?.isTrackingPresent;
      break;
    }
    case SHIPMENT_TYPE.BL:
    default:  {
      isTrackingPresent = item?.isInfoPresent;
      pendingTrackingCount = item?.pendingTrackingCount;
      break;
    }
  }
  return !isTrackingPresent || pendingTrackingCount;
}

const handleShipmentData = (shipmentData, key: string, tabId: string) => (dispatch) => {
  const shipmentList = shipmentData[key];
  if (shipmentList?.length) {
    const tobePolled : string[] = [];
    shipmentList.forEach((item: CardData, index: number) => {
      const itemId = item.id;
      if (conditionOne(item, tabId)) {
        (itemId) && tobePolled.push(itemId);
      }
      if (conditionTwo(item, tabId)) {
        (itemId) && tobePolled.push(itemId);
      }
    });
    dispatch({
      data: tobePolled,
      type: SHIPMENT_TRACKING_SET_POLLING_REMAINING,
    });
    if (isValidArray(tobePolled)) dispatch(startPoller());
  }
};

const handleData = (tabId: string, data) => (dispatch) => {
  switch (tabId) {
    case SHIPMENT_TYPE.BL: dispatch(handleShipmentData(data, 'data', tabId));
    case SHIPMENT_TYPE.HBL: dispatch(handleShipmentData(data, 'data', tabId));
    case SHIPMENT_TYPE.SHIPMENT: dispatch(handleShipmentData(data, 'data', tabId));
    case SHIPMENT_TYPE.CONTAINER: dispatch(handleShipmentData(data, 'containerList', tabId));
    default: dispatch(startPoller());
  }
};

export const showFetchingData = (shipments: Record<string, boolean>) => (dispatch, getState) => {
  const { tabId } = getState().shipmentTrackingReducer.selectedFilters;
  const data = [...getState().shipmentTrackingReducer[tabId].items];
  const updatedData = data.map((item) => {
    if (shipments[item.id || item.shipmentId]) {
      return {
        ...item,
        isTrackingFailed: false,
        isTrackingPresent: false,
        isInfoFetchFailed: false,
        isInfoPresent: false,
      };
    } return item;
  });
  dispatch({
    data: updatedData,
    type: SHIPMENT_TRACKING_FETCHING_DATA,
  });
};

export const startPoller = (params?: BulkApiRequest) => (dispatch) => {
  const pollingFrequncy = params?.shipmentId ? 5000 : CT_POLLING_FREQUENCY;
  if (!pollingAgent) {
    pollingAgent = setTimeout(() => dispatch(pollApi(params)), pollingFrequncy);
  }
};

export const stopPolling = () => () => {
  if (pollingAgent) {
    clearTimeout(pollingAgent);
    pollingAgent = null;
  }
};

function handlePollingApi(shipmentData: CardData[], pollingData: CardData[], tabId: string) {
  const tobePolled: string[] = [];
  pollingData.forEach((pollingItem: CardData) => {
    shipmentData.forEach((shipment: CardData, index: number) => {
      if (String(pollingItem.id) === String(shipment.id) && pollingItem.id) {
        shipmentData[index] = pollingItem;
        const itemId = pollingItem.id;
        if (conditionOne(pollingItem , tabId)) {
          tobePolled.push(itemId);
        }
        if (conditionTwo(pollingItem, tabId)) {
          tobePolled.push(itemId);
        }
      }
    });
  });
  return {
    tobePolled,
    updatedData: getUniqFromArray(shipmentData, 'id'),
  };
}

export const pollApi = (params?: BulkApiRequest) => async (dispatch, getState) => {
  LogRocket.track('Shipment Polling');
  const {
    trackingListRemaining,
    selectedFilters,
  } = getState().shipmentTrackingReducer;

  const { tabId } = selectedFilters;
  const { shipmentId, shipmentType } = params || {};

  let response;
  if (tabId === SHIPMENT_TYPE.CONTAINER) {
    let containerIdList = trackingListRemaining?.filter(onlyUnique);
    if (!isValidArray(containerIdList) && shipmentId) containerIdList = [shipmentId];
    if (isValidArray(containerIdList)) {
      response = await containerPoll({
        containerIdList,
      });
    }
  } else if (tabId === SHIPMENT_TYPE.SHIPMENT) {
    const shipmentIds = trackingListRemaining || [shipmentId];
    if (isValidArray(shipmentIds)) {
      response = await getShipmentPollData({
        shipmentIds,
      });
    }
  } else {
    const blIdList = trackingListRemaining?.filter(onlyUnique);
    if (isValidArray(blIdList)) {
      response = await getGlobalSummaryStats(blIdList);
    }
  }

  const shipmentData = [...(getState()?.shipmentTrackingReducer[tabId]?.items || [])];

  if (response?.isSuccess && pollingAgent) {
    const { updatedData, tobePolled } = handlePollingApi(shipmentData, response.data, tabId);
    dispatch({
      data: tobePolled,
      type: SHIPMENT_TRACKING_SET_POLLING_REMAINING,
    });
    dispatch({
      data: updatedData,
      type: SHIPMENT_TRACKING_SET_POLLING_DATA,
    });
    dispatch(stopPolling());
    dispatch(startPoller());
  }
};

export const applyFilters = (filters: Partial<ShipmentTrackingFilters>) => (dispatch) => {
  batch(() => {
    dispatch(setFilters(filters));
    dispatch(fetchShipmentTrackingData());
  });
};

export const setPagination = pagination => async (dispatch) => {
  dispatch({
    data: pagination,
    type: SHIPMENT_TRACKING_SET_PAGINATION,
  });
  dispatch(fetchShipmentTrackingData());
};

export const resetPagination = () => async (dispatch) => {
  dispatch({
    type: SHIPMENT_TRACKING_RESET_PAGINATION,
  });
  dispatch(fetchShipmentTrackingData());
};

export const resetAndSetPagination = resultPerPage => (dispatch) => {

  dispatch({
    resultPerPage,
    type: SHIPMENT_TRACKING_BATCH_SIZE,
  });
  dispatch(fetchShipmentTrackingData());
};

export const resetFilters = (tabId: string, pageOnly = false) => async (dispatch) => {
  dispatch({
    tabId,
    pageOnly,
    type: SHIPMENT_TRACKING_RESET_FILTERS,
  });
  dispatch(fetchShipmentTrackingData());
};

export const fetchDataFromPrevPage = (isReload = false) => async (dispatch) => {
  dispatch({
    isReload,
    type: SHIPMENT_TRACKING_SET_PREV_PAGINATION,
  });
  dispatch(fetchShipmentTrackingData(true));
};

export const resetSelectedItems = () => (dispatch) => {
  dispatch({
    selectedItems: [],
    type: SHIPMENT_TRACKING_CHECK_CHANGED,
  });
};

export const changeSelection = (
  selectedItems: Record<string, CardData>,
) => (dispatch) => {
  dispatch({
    selectedItems,
    type: SHIPMENT_TRACKING_CHECK_CHANGED,
  });
};

export const retryTracking = (shipments: BulkApiRequest[]) => async (dispatch, getState) => {
  let tobePolled: string[] = getState().shipmentTrackingReducer.trackingListRemaining;
  tobePolled = tobePolled.concat(shipments?.map(item => (item.shipmentId)));
  tobePolled = tobePolled?.filter(onlyUnique);
  dispatch({
    data: tobePolled,
    type: SHIPMENT_TRACKING_SET_POLLING_REMAINING,
  });
};

export const fetchSummaryMetrics = (filters = {}) => async (dispatch, getState) => {
  const { selectedFilters } = getState().shipmentTrackingReducer;
  const apiBody = getCommonFilters(selectedFilters);
  const response = await getGlobalSummary({ ...apiBody, ...filters });
  if (response.isSuccess) {
    dispatch({
      type: SHIPMENT_TRACKING_SUMMARY_METRIC_UPDATE,
      data: response.data,
    });
  }
};

export const resetShipmentData = () => (dispatch) => {
  dispatch({
    type: SHIPMENT_TRACKING_RESET_DETAIL_DATA,
  });
};

export const setSearchFilters = filters => (dispatch) => {
  dispatch({
    filters,
    type: SHIPMENT_TRACKING_SEARCH_FILTERS,
  });
};

export const setFilters = (filters = {}) => ({
  type: SHIPMENT_TRACKING_FILTERS,
  data: filters,
});

